import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

import { Button } from '../../../common/Button';
import { Spinner } from '../../../components/Spinner';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../services/supabase';

export function GoogleCalendarIntegration(): JSX.Element {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkIntegrationStatus = async (): Promise<void> => {
    if (user) {
      setIsLoading(true);
      const { data, error: integrationError } = await supabase
        .from('google_calendar_integrations')
        .select('id, user_id, access_token')
        .eq('user_id', user.id)
        .single();

      if (integrationError && integrationError.code !== 'PGRST116') {
        console.error('Error checking integration status:', integrationError);
        setError('Failed to check integration status. Please try again.');
      } else {
        setIsConnected(!!data);
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkIntegrationStatus().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const { error: connectError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          scopes: ['https://www.googleapis.com/auth/calendar.readonly'].join(
            ' ',
          ),
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
            response_type: 'code', // Add this to ensure we get an authorization code
          },
          redirectTo: `${window.location.origin}/auth/google/callback`,
        },
      });
      if (connectError) throw connectError;
    } catch (connectError) {
      console.error('Error initiating Google OAuth:', connectError);
      setError('Failed to connect to Google Calendar. Please try again.');
      toast.error('Failed to connect to Google Calendar. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await supabase
        .from('google_calendar_integrations')
        .delete()
        .eq('user_id', user?.id);

      setIsConnected(false);
      setError(null);
      toast.success('Google Calendar disconnected successfully');
    } catch (disconnectError) {
      console.error('Error disconnecting Google Calendar:', disconnectError);
      toast.error('Failed to disconnect Google Calendar. Please try again.');
      setError('Failed to disconnect Google Calendar. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  if (isLoading) {
    <div className="flex  items-center justify-center w-full min-h-[200px] bg-surface-0 p-6 rounded-lg border border-border-gray ">
      <Spinner />
    </div>;
  }
  return (
    <div className="flex flex-col gap-2 items-start justify-center w-full bg-surface-0 p-6 rounded-lg border border-border-gray ">
      <h3 className="text-xl font-semibold mb-2 w-full">Google Calendar</h3>
      {error && <p className="text-error">{error}</p>}
      {isConnected ? (
        <div>
          <p>Your Google Calendar is connected. Email: {user?.email}</p>
          <Button
            onClick={() => {
              const handle = async (): Promise<void> => {
                await handleDisconnect();
              };
              handle().catch(console.error);
            }}
            variant="secondary"
            size="md"
          >
            Disconnect Google Calendar
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 items-start justify-center w-full">
          <p>Connect your Google Calendar to sync your meetings.</p>
          <Button
            onClick={() => {
              handleConnect().catch(console.error);
            }}
            variant="primary"
            size="md"
          >
            Connect Google Calendar
          </Button>
        </div>
      )}
    </div>
  );
}

export default GoogleCalendarIntegration;
